export interface Indicador {
  medida: string
  formato: 'numero' | 'percentual' | 'moeda' | string
  maiorMelhor: boolean
  valor?: number | null
  anterior?: number | null
  serie: { fim: string; valor?: number | null }[]
  semValorNaRegra: boolean
}

const compacto = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })
const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 })
const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', maximumFractionDigits: 1 })

/** O valor no formato do indicador. Percentual vem do modelo como fração (0,87 = 87%). */
export function formatar(v: number | null | undefined, formato: string): string {
  if (v == null || !isFinite(v)) return '—'
  if (formato === 'percentual') return `${inteiro.format(v * 100)}%`
  if (formato === 'moeda') return moeda.format(v)
  return Math.abs(v) >= 10000 ? compacto.format(v) : inteiro.format(v)
}

/**
 * A variação contra a semana anterior: em pontos percentuais para percentual, em % para o resto.
 * `bom` diz se a mudança é boa, conforme "maior é melhor".
 */
export function variacao(i: Pick<Indicador, 'valor' | 'anterior' | 'formato' | 'maiorMelhor'>): { texto: string; bom: boolean | null } | null {
  if (i.valor == null || i.anterior == null) return null
  const dif = i.valor - i.anterior
  if (dif === 0) return { texto: 'igual à semana anterior', bom: null }
  const bom = (dif > 0) === i.maiorMelhor
  const sinal = dif > 0 ? '+' : '−'
  if (i.formato === 'percentual') return { texto: `${sinal}${inteiro.format(Math.abs(dif) * 100)} p.p.`, bom }
  if (i.anterior === 0) return { texto: dif > 0 ? 'subiu' : 'caiu', bom }
  return { texto: `${sinal}${inteiro.format(Math.abs(dif / i.anterior) * 100)}%`, bom }
}

/** Os pontos da linha (SVG) das 12 semanas, num quadro de largura x altura. Semana sem valor fica de fora. */
export function pontosDaLinha(serie: { valor?: number | null }[], largura: number, altura: number): string {
  const vals = serie.map(p => p.valor).filter((v): v is number => v != null && isFinite(v))
  if (vals.length < 2) return ''
  const min = Math.min(...vals), max = Math.max(...vals)
  const faixa = max - min || 1
  const passo = largura / Math.max(1, serie.length - 1)
  return serie
    .map((p, i) => (p.valor == null || !isFinite(p.valor) ? null : `${(i * passo).toFixed(1)},${(altura - 2 - ((p.valor - min) / faixa) * (altura - 4)).toFixed(1)}`))
    .filter(Boolean)
    .join(' ')
}
